import { SetStaffingPlanLink } from './CensusRequirementView.js'
import { TIER_BG_CLASS, TIER_LABELS } from '../../core/index.js'
import type { CensusBlocker, CensusEvaluation, CensusEvaluationLine, CensusFlexedWorker, FlexStepKey } from '../types.js'
import { SHIFT_TYPE_LABELS } from './preferencesText.js'

/**
 * What a census would change on the released month (census response plan
 * F1), under the What-if's requirement: the verdict with the scheduled HPPD
 * before and after, the lines, who would be floated or flexed and why, the
 * seats that would post and who would be offered them, where the minimum
 * holds, and what keeps it from happening by itself.
 */

const VERDICT_TEXT: Record<CensusEvaluation['verdict'], string> = {
  no_change: 'Nothing to change',
  rise: 'The census rises: seats to post',
  fall: 'The census falls: shifts to release',
  nothing_to_judge: 'Nothing to judge yet',
}
const VERDICT_CLASS: Record<CensusEvaluation['verdict'], string> = { no_change: 'text-green', rise: 'text-marine', fall: 'text-coral', nothing_to_judge: 'text-ink-mid' }
const BLOCKER_TEXT: Record<CensusBlocker, string> = {
  not_released: 'The month is not released, so there is nothing to flex or post yet.',
  no_staffing_plan: 'No staffing plan: set one under Configuration › Scheduling.',
  inside_lock: 'The shift starts too soon for the engine to change it: only a supervisor’s override changes it now.',
  inside_notice: 'Inside the notice hours: the change is proposed for a person, never automatic.',
}
const STEP_TEXT: Record<FlexStepKey, string> = { volunteers: 'volunteered', agency: 'agency', prn: 'PRN', tier: 'lowest tier', rotation: 'rotation', seniority: 'least senior', overtime: 'over the weekly line', part_time: 'part time' }

const hppd = (value: number | null): string => (value === null ? '—' : value.toFixed(2))
const EMPLOYMENT_SHORT: Record<CensusFlexedWorker['employmentType'], string> = { full_time: 'FT', part_time: 'PT', prn: 'PRN', agency: 'agency' }

function TierChip({ worker }: { worker: CensusFlexedWorker }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-ink">
      <span className={`inline-block h-2 w-2 rounded-full ${TIER_BG_CLASS[worker.tier]}`} aria-hidden />
      {TIER_LABELS[worker.tier]} {worker.score}
      {worker.employmentType !== 'full_time' && <span className="font-normal text-ink-mid">· {worker.employmentType === 'part_time' ? 'PT' : worker.employmentType.toUpperCase()}</span>}
    </span>
  )
}

function describeLine(line: CensusEvaluationLine): string {
  const base = `${SHIFT_TYPE_LABELS[line.shiftType]} ${line.role}${line.short ? ` short shift (${line.shiftHours} h)` : ''}: ${line.staffed} held, ${line.required} required`
  if (line.delta === 'same') {
    return `${base}${line.floorHeld ? ' (the minimum line)' : ''}`
  }
  return `${base} · ${line.delta === 'add' ? 'add' : 'release'} ${line.count}`
}

export function CensusEvaluationPanel({ evaluation, configHref }: { evaluation: CensusEvaluation; /** Given, a missing plan comes with the button to set it. */ configHref?: string }) {
  const changing = evaluation.lines.filter((line) => line.delta !== 'same')
  return (
    <div className="mt-2 border-t border-marine/10 pt-2 text-[11px]">
      <div className={`font-bold ${VERDICT_CLASS[evaluation.verdict]}`}>
        {VERDICT_TEXT[evaluation.verdict]}
        {evaluation.verdict !== 'nothing_to_judge' && (
          <span className="font-normal text-ink-mid">
            {' '}· scheduled {hppd(evaluation.hppd.scheduled)} HPPD against {hppd(evaluation.hppd.target)}
            {evaluation.verdict !== 'no_change' && ` → ${hppd(evaluation.hppd.after)} after`}
            {' '}· {evaluation.mode === 'automatic' ? 'automatic' : 'proposed for a person'}
          </span>
        )}
      </div>
      {evaluation.blockers.map((blocker) => (
        <div key={blocker} className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-[10px] text-coral">{BLOCKER_TEXT[blocker]}</p>
          {blocker === 'no_staffing_plan' && configHref && <SetStaffingPlanLink configHref={configHref} />}
        </div>
      ))}
      {changing.length > 0 && (
        <ul className="mt-1 text-ink-mid">
          {changing.map((line) => (
            <li key={`${line.shiftType}-${line.role}-${line.short ? 'short' : 'full'}`}>{describeLine(line)}</li>
          ))}
        </ul>
      )}
      {evaluation.releases.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-coral">Released, in order</div>
          <ol className="divide-y divide-marine/10">
            {evaluation.releases.map((release, index) => (
              <li key={release.shiftId} className="flex flex-wrap items-center justify-between gap-2 py-1">
                <span className="min-w-0">
                  <span className="text-ink-mid">{index + 1}.</span> <span className="font-bold text-ink">{release.worker.name}</span> <TierChip worker={release.worker} />
                  <span className="block text-[10px] text-ink-mid">
                    {SHIFT_TYPE_LABELS[release.shiftType]} {release.role} · {STEP_TEXT[release.step]}, {release.because} · {release.hours} h
                  </span>
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${release.action === 'float' ? 'bg-marine/10 text-marine' : 'bg-coral/10 text-coral'}`}>{release.action === 'float' ? `Floats to ${release.floatTo?.unitId ?? 'another unit'}` : 'Flexed off'}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {evaluation.additions.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-marine">Posted to the board</div>
          <ul className="divide-y divide-marine/10">
            {evaluation.additions.map((addition) => (
              <li key={`${addition.shiftType}-${addition.role}-${addition.short ? 'short' : 'full'}`} className="py-1">
                <span className="font-bold text-ink">
                  {addition.seats} {addition.role} {SHIFT_TYPE_LABELS[addition.shiftType].toLowerCase()} {addition.short ? `short ${addition.seats === 1 ? 'shift' : 'shifts'} ${addition.short.start}–${addition.short.end}` : addition.seats === 1 ? 'seat' : 'seats'}
                </span>{' '}
                <span className="text-ink-mid">· {addition.hours} h</span>
                <div className="text-[10px] text-ink-mid">
                  {addition.offeredFirst.length === 0 ? 'Nobody the facility’s add order names is off that day; it goes to everyone of the role first come.' : `Offered first, in the facility’s add order, to ${addition.offeredFirst.map((worker) => `${worker.name} (${EMPLOYMENT_SHORT[worker.employmentType]}, ${worker.score})`).join(', ')}`}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {evaluation.holds.length > 0 && (
        <p className="mt-2 text-[10px] text-ink-mid">
          The minimum staffing holds on {evaluation.holds.map((line) => `${SHIFT_TYPE_LABELS[line.shiftType].toLowerCase()} ${line.role} (${line.required})`).join(', ')}: nothing is released there whatever the census.
        </p>
      )}
    </div>
  )
}
